import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AuthShell, GoogleButton } from "@/pages/Login";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success("Welcome to Connect!");
      navigate("/app/profile");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Join Connect" subtitle="Create a free account — everyone is welcome.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="font-semibold text-forest-900 mb-1.5 block">Full name</label>
          <div className="relative">
            <User size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest-500" strokeWidth={2.5} />
            <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full min-h-[56px] text-lg pl-12 pr-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-white" placeholder="Your name" />
          </div>
        </div>
        <div>
          <label className="font-semibold text-forest-900 mb-1.5 block">Email</label>
          <div className="relative">
            <Mail size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest-500" strokeWidth={2.5} />
            <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full min-h-[56px] text-lg pl-12 pr-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-white" placeholder="you@example.com" />
          </div>
        </div>
        <div>
          <label className="font-semibold text-forest-900 mb-1.5 block">Password</label>
          <div className="relative">
            <Lock size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest-500" strokeWidth={2.5} />
            <input data-testid="register-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full min-h-[56px] text-lg pl-12 pr-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-white" placeholder="At least 6 characters" />
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="register-submit"
          className="w-full min-h-[56px] rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-transform active:scale-95 disabled:opacity-60">
          {busy ? "Creating account…" : "Create my account"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="h-px bg-border flex-1" /><span className="text-forest-500 font-medium">or</span><div className="h-px bg-border flex-1" />
      </div>
      <GoogleButton />
      <p className="text-center text-forest-700 mt-6 text-lg">
        Already a member? <Link to="/login" className="text-terracotta font-bold" data-testid="go-login">Log in</Link>
      </p>
    </AuthShell>
  );
}
